'use client';

import LinkRouter from 'next/link';
import { useRouter } from 'next/navigation';
import { useContext, useMemo, useState } from 'react';

import { BuyerTypeEnum, FundingKey, JourneyType, ProductType, ResourceType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import { Button, ButtonVariant, Icon, IconName, Link, Text } from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { EasyTradeInChoice } from '@app-types/easy-trade-in';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull, ProductStockDetails } from '@app-types/product';

import { scrollToHash } from '@helpers/document';
import { paths } from '@helpers/path';
import { LockedPlanValue, getBlockedPlans } from '@helpers/plan-lock';
import { getNeededElementGencodes, getSimilarProductsLink } from '@helpers/product';

import { EASY_TRADE_IN_FORM_ID } from '@constants/easy-trade-in';
import { KidsWatch, OPTION_LOCKED_ID } from '@constants/plan';
import { TradeInStep } from '@constants/trade-in';

import { CTASource } from '@product-detail/constants';
import {
    clearTradeInProduct,
    getTradeInLastStep,
    getTradeInRecoveryId,
    isOutOfStock,
    sendProductCTATracking,
} from '@product-detail/helpers';
import { ClickEvent, OnClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

export type EasyTradeInCtaState = {
    isEnabled: boolean;
    isEligible: boolean;
    choice: EasyTradeInChoice;
    isConfirmed: boolean;
    partnerId?: string;
    triggerReturnHint?: () => void;
};

export type ProductCTAProps = {
    type: WallType;
    source: CTASource;
    openShopAvailabilityModal: ClickEvent;
    currentProduct: ProductChildFull;
    plan?: MobilePlanType | null;
    contract?: ContractType;
    isRenewal: boolean;
    fundingKey: FundingKey | undefined;
    idPu?: string;
    simulatedPortabilitySelected: boolean;
    easyTradeIn?: EasyTradeInCtaState;
    stockDetails?: ProductStockDetails;
};

export function ProductCTA({
    type,
    source,
    openShopAvailabilityModal,
    currentProduct,
    plan,
    contract,
    isRenewal,
    fundingKey,
    idPu,
    simulatedPortabilitySelected,
    easyTradeIn,
    stockDetails: _stockDetails,
}: Readonly<ProductCTAProps>) {
    const {
        addResourceToProductMutation,
        cart,
        addProductsToCartMutation,
        removeProductFromCartMutation,
        setFundingModeMutation,
        addBuyerMutation,
        setTradeInMutation,
        removeResourceFromProductMutation,
    } = useContext(CartContext);
    const router = useRouter();

    const [isLoading, setIsLoading] = useState(false);

    const isEasyTradeInGated =
        !!easyTradeIn?.isEnabled &&
        easyTradeIn.isEligible &&
        easyTradeIn.choice === EasyTradeInChoice.RETURN &&
        !easyTradeIn.isConfirmed;

    const isFullwidth = useMemo(() => source === CTASource.TICKET, [source]);
    const hasMobileIcon = useMemo(() => source === CTASource.STICKY, [source]);

    const journey = useMemo(() => {
        return isRenewal
            ? cart?.getJourneyOfType(JourneyType.RENEWAL)
            : cart?.getJourneyOfType(JourneyType.MOBILE_ACQUISITION);
    }, [cart, isRenewal]);

    const stockDetails = _stockDetails ?? currentProduct?.stockDetails;

    if (!stockDetails) {
        return (
            <Button variant={ButtonVariant.CONVERSION} fullwidth={isFullwidth} loading>
                Ajouter au panier
            </Button>
        );
    }

    const { stock, isAvailableInStore } = stockDetails;

    const handleClick = async (event: OnClickEvent) => {
        if (isOutOfStock(stock.status)) {
            openShopAvailabilityModal(event);
        } else if (isEasyTradeInGated) {
            window.location.hash = `#${EASY_TRADE_IN_FORM_ID}`;
            scrollToHash();
            easyTradeIn?.triggerReturnHint?.();
        } else {
            sendProductCTATracking(isRenewal, stock.label, source);

            setIsLoading(true);

            if (plan === null && journey) {
                const productsToDelete = journey.getProductsByType(ProductType.MAIN_MOBILE_OFFER) ?? [];

                await Promise.all(
                    productsToDelete.map((product) =>
                        removeProductFromCartMutation.mutateAsync({ journey: journey.type, gencode: product.gencode }),
                    ),
                );
            }

            const lastStep = getTradeInLastStep();

            if (lastStep !== TradeInStep.SEARCH && lastStep !== TradeInStep.CONFIRMED) {
                clearTradeInProduct();
            }

            const autoAdd = getNeededElementGencodes(plan);

            if (type === WallType.KIDS_WATCH) {
                autoAdd.push(KidsWatch.OPTION_GENCODE);
            }

            if (plan && plan.locked === LockedPlanValue.LOCK_OPTIONAL) {
                const blockedsPlans = getBlockedPlans();
                if (blockedsPlans[plan.entityId ?? '']) {
                    autoAdd.push(OPTION_LOCKED_ID);
                }
            }

            const existingFaiJourney = cart?.getJourneyOfType(JourneyType.FAI_ACQUISITION);
            const hasOnlyFaiJourney = existingFaiJourney && cart?.journeys.length === 1;

            let journeyToAddProduct: JourneyType;
            if (hasOnlyFaiJourney) {
                journeyToAddProduct = JourneyType.FAI_ACQUISITION;
            } else if (isRenewal) {
                journeyToAddProduct = JourneyType.RENEWAL;
            } else {
                journeyToAddProduct = JourneyType.MOBILE_ACQUISITION;
            }

            addProductsToCartMutation
                .mutateAsync({
                    journey: journeyToAddProduct,
                    gencodes: plan
                        ? [currentProduct.gencode, plan.gencode, ...autoAdd]
                        : [currentProduct.gencode, ...autoAdd],
                    contractId: contract?.id,
                })
                .then(async (newCart) => {
                    if (!newCart?.buyer && idPu) {
                        await addBuyerMutation.mutateAsync({
                            type: BuyerTypeEnum.CUSTOMER,
                            id: idPu,
                        });
                    }

                    const journeyType = isRenewal ? JourneyType.RENEWAL : JourneyType.MOBILE_ACQUISITION;
                    const currentTradeInPartnerId = cart?.getJourneyOfType(journeyType)?.tradeIn?.partnerId ?? null;

                    if (easyTradeIn?.isConfirmed && easyTradeIn.partnerId) {
                        if (currentTradeInPartnerId !== easyTradeIn.partnerId) {
                            await setTradeInMutation.mutateAsync({
                                journey: journeyType,
                                partnerId: easyTradeIn.partnerId,
                                isEasy: true,
                            });
                        }
                    } else {
                        const recoveryId = getTradeInRecoveryId();
                        if (currentTradeInPartnerId !== recoveryId) {
                            await setTradeInMutation.mutateAsync({ journey: journeyType, partnerId: recoveryId });
                        }
                    }

                    if (plan && !isRenewal) {
                        if (simulatedPortabilitySelected) {
                            await addResourceToProductMutation.mutateAsync({
                                gencode: plan.gencode,
                                type: ResourceType.PORTABILITY_SIMULATED,
                            });
                        } else {
                            await removeResourceFromProductMutation.mutateAsync({
                                gencode: plan.gencode,
                                type: ResourceType.PORTABILITY_SIMULATED,
                            });
                        }
                    }

                    if (fundingKey) {
                        await setFundingModeMutation.mutateAsync({
                            fundingKey: fundingKey,
                            callbackUrl: `${window.location.origin}/tunnel/redirect`, // Unused in virtual cart
                        });
                    }
                    if (type === WallType.PHONE) {
                        router.push(paths.crossSell('product-detail'));
                    } else {
                        window.location.assign(paths.cart);
                    }
                })
                .catch(() => {
                    router.push(paths.productWall(type));
                })
                .finally(() => setIsLoading(false));
        }
    };

    return (
        <>
            {isOutOfStock(stock.status) && !isAvailableInStore && (
                <Link
                    href={getSimilarProductsLink(type, currentProduct, isRenewal)}
                    routerLink={LinkRouter}
                    className='has-no-text-decoration'
                >
                    <Button variant={ButtonVariant.CONVERSION} fullwidth={isFullwidth} name={stock.cart}>
                        <ButtonContent wording={stock.cart} icon={stock.icon} hasMobileIcon={hasMobileIcon} />
                    </Button>
                </Link>
            )}
            {isOutOfStock(stock.status) && isAvailableInStore && (
                <Button
                    variant={ButtonVariant.CONVERSION}
                    fullwidth={isFullwidth}
                    onClick={handleClick}
                    loading={isLoading}
                    name={stock.cart}
                >
                    <ButtonContent
                        wording='Voir les disponibilités en boutique'
                        icon={stock.icon}
                        hasMobileIcon={hasMobileIcon}
                    />
                </Button>
            )}
            {!isOutOfStock(stock.status) && (
                <Button
                    variant={ButtonVariant.CONVERSION}
                    fullwidth={isFullwidth}
                    onClick={handleClick}
                    loading={isLoading}
                    name={stock.cart}
                    data-cy='product-add-to-cart'
                >
                    <ButtonContent wording={stock.cart} icon={stock.icon} hasMobileIcon={hasMobileIcon} />
                </Button>
            )}
        </>
    );
}

function ButtonContent({
    wording,
    icon,
    hasMobileIcon,
}: Readonly<{ wording: string; icon: IconName; hasMobileIcon: boolean }>) {
    return (
        <>
            <Text className={hasMobileIcon ? 'is-hidden-mobile' : ''} marginless>
                {wording}
            </Text>
            {hasMobileIcon && <Icon name={icon} className='is-hidden-tablet' />}
        </>
    );
}
